import { useState, useCallback } from 'react';
import generateProfessionalPDF from '../pdfGenerator';
import { savePdf } from '../api/pdfApi'; // ✅ BACKEND

export const useWorkOrderSubmission = ({
    form,
    selectedParts,
    manualParts,
    userRole,
    currentUser,
    onCreate,
    onClose,
    resetForm,
    resetParts,
    setActiveSection
}) => {
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

    // ✅ Nada obligatorio (compat)
    const validateForm = useCallback(() => {
        setFormErrors({});
        return {};
    }, []);

    const clearErrors = useCallback(() => {
        setFormErrors({});
    }, []);

    const generateWorkOrderNumber = useCallback(() => {
        const timestamp = Date.now().toString(36);
        const random = Math.random().toString(36).slice(2, 8);
        return `WO-${timestamp}-${random}`.toUpperCase();
    }, []);

    const safeString = (v) => (typeof v === 'string' ? v.trim() : '');
    const safeUpper = (v) => safeString(v).toUpperCase();

    const handleSubmit = useCallback(async () => {
        if (isSubmitting || isGeneratingPDF) return;

        validateForm();
        setIsSubmitting(true);

        try {
            const createdAtIso = new Date().toISOString();

            const workOrderNumber =
                safeString(form.workOrderNumber).length > 0
                    ? safeUpper(form.workOrderNumber)
                    : generateWorkOrderNumber();

            const finalForm = {
                ...form,
                workOrderNumber,
                client: safeString(form.client),
                plates: safeUpper(form.plates),
                vin: safeUpper(form.vin),
                unit: safeString(form.unit),
                engine: safeString(form.engine),
                model: safeString(form.model),
                mileage: form.mileage ?? '',
                driverName: safeString(form.driverName),
                driverDescription: safeString(form.driverDescription),
                diagnosticType: safeString(form.diagnosticType),
                technicalDescription: safeString(form.technicalDescription),
                additionalObservations: safeString(form.additionalObservations),

                partsUsed: Array.isArray(selectedParts) ? selectedParts : [],
                manualParts: Array.isArray(manualParts) ? manualParts : [],

                status: form.status || 'diagnostico_inicial',
                createdAt: form.createdAt || createdAtIso,
                date: form.date,
                createdBy: currentUser?.name || 'Sistema',
                userRole
            };

            // 1) Guardar en tu backend (orden/reporte)
            await onCreate(finalForm);

            // 2) Generar PDF (visual)
            setIsGeneratingPDF(true);
            await generateProfessionalPDF(finalForm);

            // 3) Guardar en historial BACKEND (/api/pdfs)
            const record = {
                id: `service-report-${workOrderNumber}`,
                name: `Reporte_${workOrderNumber}.pdf`,
                createdAt: createdAtIso,
                type: 'service-report',
                source: 'WorkOrderModal',
                client: finalForm.client || '',
                folio: finalForm.workOrderNumber || '',
                total: null,
                blobOrUrl: null,
                meta: {
                    rawData: finalForm
                }
            };

            try {
                await savePdf(record);
            } catch (e) {
                console.error('[useWorkOrderSubmission] savePdf backend error:', e);
                // no bloquees al usuario si el PDF no se pudo guardar en BD
            }

            // 4) Limpieza UI
            resetForm();
            resetParts();
            onClose();
        } catch (error) {
            console.error('[useWorkOrderSubmission] Error creating work order:', error);
            alert('Error al crear el reporte de trabajo. Por favor intenta de nuevo.');
        } finally {
            setIsSubmitting(false);
            setIsGeneratingPDF(false);
        }
    }, [
        isSubmitting,
        isGeneratingPDF,
        validateForm,
        form,
        selectedParts,
        manualParts,
        userRole,
        currentUser,
        onCreate,
        onClose,
        resetForm,
        resetParts,
        setActiveSection,
        generateWorkOrderNumber
    ]);

    return {
        formErrors,
        isSubmitting,
        isGeneratingPDF,
        handleSubmit,
        clearErrors
    };
};
